// Preload utilities for improving performance
class PreloadManager {
  constructor() {
    this.preloadedModules = new Map();
    this.preloadedData = new Map();
  }

  // Preload a module
  async preloadModule(importer) {
    try {
      const module = await importer();
      // Use the module path as key (or a unique identifier)
      const moduleKey = this.generateModuleKey(importer);
      this.preloadedModules.set(moduleKey, module);
      return module;
    } catch (error) {
      console.error('Error preloading module:', error);
      return null;
    }
  }

  // Get a preloaded module
  getPreloadedModule(importer) {
    const moduleKey = this.generateModuleKey(importer);
    return this.preloadedModules.get(moduleKey);
  }

  // Check if a module is preloaded
  isModulePreloaded(importer) {
    const moduleKey = this.generateModuleKey(importer);
    return this.preloadedModules.has(moduleKey);
  }

  // Generate a key for a module
  generateModuleKey(importer) {
    // This is a simple approach - in a real app you might want to use
    // the actual path or a hash of the importer function
    return importer.toString();
  }

  // Preload data
  preloadData(key, dataPromise) {
    if (this.preloadedData.has(key)) {
      return this.preloadedData.get(key);
    }

    // Store the promise, not the resolved value
    this.preloadedData.set(key, dataPromise);
    return dataPromise;
  }

  // Get preloaded data
  getPreloadedData(key) {
    return this.preloadedData.get(key);
  }

  // Check if data is preloaded
  isDataPreloaded(key) {
    return this.preloadedData.has(key);
  }

  // Clear preloaded data
  clear() {
    this.preloadedModules.clear();
    this.preloadedData.clear();
  }
}

// Create a global preload manager instance
export const preloadManager = new PreloadManager();

// Function to preload common modules based on user role
export const preloadCommonModules = async (userRole = 'student') => {
  if (userRole === 'admin') {
    // Preload admin modules
    await Promise.all([
      preloadManager.preloadModule(() => import('../pages/admin/dashboard.js')),
      preloadManager.preloadModule(() => import('../pages/admin/students.js')),
      preloadManager.preloadModule(() => import('../pages/admin/assignments.js')),
      preloadManager.preloadModule(() => import('../pages/admin/content.js'))
    ]);
  } else {
    // Preload student modules
    await Promise.all([
      preloadManager.preloadModule(() => import('../pages/student/dashboard.js')),
      preloadManager.preloadModule(() => import('../pages/student/flashcards.js')),
      preloadManager.preloadModule(() => import('../pages/student/quiz.js')),
      preloadManager.preloadModule(() => import('../pages/student/lessons.js'))
    ]);
  }
};